import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/api/client";
import { toast } from "sonner";
import { Section, Button, Input, Select, Badge, Empty } from "@/components/ui/kit";
import { fmtCurrency, fmtDate } from "@/utils/format";
import { Edit2, Save, Trash2, X, Info } from "lucide-react";

export default function DonationView() {
  const { id } = useParams();
  const nav = useNavigate();
  
  // Data states
  const [donation, setDonation] = useState(null);
  const [purposes, setPurposes] = useState([]);
  const [donors, setDonors] = useState([]);
  const [years, setYears] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [amountTypes, setAmountTypes] = useState([]);
  const [err, setErr] = useState(null);
  
  // UI states
  const [isEditing, setIsEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  // Form state for editing
  const [f, setF] = useState({
    donorId: "", donationDate: "", donatedAmount: "", paymentModeId: "",
    sectorId: "", projectId: "", activityId: "", donationType: "SELF",
    matchedByDonorId: "", matchedAmount: "", academicYearId: "",
    amountTypeId: "", transactionReferenceNumber: ""
  });

  useEffect(() => {
    loadAllData();
    // eslint-disable-next-line
  }, [id]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [pRes, dRes, yRes, payRes, amtRes, donRes] = await Promise.all([
        api.get("/donation-purposes"),
        api.get("/donors", { params: { size: 500 } }),
        api.get("/master/academic-years"),
        api.get("/master/payment-modes"),
        api.get("/master/amount-types"),
        api.get(`/donations/${id}/journey`)
      ]);

      const allPurposes = pRes.data.data;
      setPurposes(allPurposes);
      setDonors(dRes.data.data.content);
      setYears(yRes.data.data);
      setPaymentModes(payRes.data.data || payRes.data);
      setAmountTypes(amtRes.data.data || amtRes.data);
      
      const data = donRes.data.data;
      setDonation(data);
      initForm(data, allPurposes);
      
    } catch (error) {
      setErr(error.response?.data?.message || "Failed to load donation details");
    } finally {
      setLoading(false);
    }
  };

  const initForm = (data, allPurposes) => {
    let sId = "", pId = "", aId = "";
    
    if (data.purposeId) {
      const p = allPurposes.find(x => x.id === data.purposeId);
      if (p) {
        if (p.type === "ACTIVITY") {
          aId = p.id;
          pId = p.parentId;
          const proj = allPurposes.find(x => x.id === p.parentId);
          if (proj) sId = proj.parentId;
        } else if (p.type === "PROJECT") {
          pId = p.id;
          sId = p.parentId;
        } else if (p.type === "SECTOR") {
          sId = p.id;
        }
      }
    }

    let dDate = data.donationDate;
    if (dDate && dDate.includes("T")) dDate = dDate.split("T")[0];

    setF({
      donorId: data.donorId || "",
      donationDate: dDate || "",
      donatedAmount: data.amount || "",
      paymentModeId: data.paymentModeId || "",
      amountTypeId: data.amountTypeId || "",
      transactionReferenceNumber: data.transactionReferenceNumber || "",
      sectorId: sId,
      projectId: pId,
      activityId: aId,
      donationType: data.donationType || "SELF",
      matchedByDonorId: data.matchedByDonorId || "", 
      matchedAmount: data.matchedAmount || "", 
      academicYearId: data.academicYearId || "",
    });
  };

  const sectors = useMemo(() => purposes.filter(p => p.type === "SECTOR"), [purposes]);
  const projects = useMemo(() => purposes.filter(p => p.type === "PROJECT" && (!f.sectorId || String(p.parentId) === String(f.sectorId))), [purposes, f.sectorId]);
  const activities = useMemo(() => purposes.filter(p => p.type === "ACTIVITY" && (!f.projectId || String(p.parentId) === String(f.projectId))), [purposes, f.projectId]);

  const selectedCurrencyCode = useMemo(() => {
    if (!f.amountTypeId) return null;
    return amountTypes.find(a => String(a.amount_type_id) === String(f.amountTypeId))?.code;
  }, [f.amountTypeId, amountTypes]);

  const upd = (k) => (e) => setF(v => ({ ...v, [k]: e.target.value }));

  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to delete Donation #${id}? This will also delete related allocations.`)) {
      try {
        await api.delete(`/donations/${id}`);
        toast.success("Donation deleted successfully");
        nav("/admin/donations");
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to delete donation");
      }
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const purposeId = f.activityId || f.projectId || f.sectorId;
    if (!f.donorId) return toast.error("Please select a donor");
    if (!purposeId) return toast.error("Please select a sector / project / activity");
    if (!f.donatedAmount) return toast.error("Please enter an amount");
    if (!f.amountTypeId) return toast.error("Please select an amount type");
    if (!f.paymentModeId) return toast.error("Please select a payment mode");

    setBusy(true);
    try {
      const payload = {
        donorId: Number(f.donorId),
        donationDate: f.donationDate,
        amount: Number(f.donatedAmount),
        currency_code: selectedCurrencyCode, 
        paymentModeId: Number(f.paymentModeId),
        amountTypeId: Number(f.amountTypeId),
        transactionReferenceNumber: f.transactionReferenceNumber,
        purposeId: Number(purposeId),
        donationType: f.donationType,
        matchedByDonorId: f.donationType === "MATCHED" && f.matchedByDonorId ? Number(f.matchedByDonorId) : null,
        matchedAmount: f.donationType === "MATCHED" && f.matchedAmount ? Number(f.matchedAmount) : null,
        academicYearId: f.academicYearId ? Number(f.academicYearId) : null,
      };
      
      await api.put(`/donations/${id}`, payload);
      toast.success("Donation updated successfully");
      setIsEditing(false);
      loadAllData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update donation");
    } finally { 
      setBusy(false); 
    }
  };

  if (err) return <Empty title="Donation not found" note={err} />;
  if (loading || !donation) return <div className="text-muted-foreground p-8">Loading donation details...</div>;

  const displayCurrencyCode = donation.amountType || donation.currencyCode || "INR";
  const isForeignCurrency = displayCurrencyCode !== "INR";
  
  // Flag to check if funds are already utilized
  const isUtilized = donation.utilizedAmount > 0;

  return (
    <div className="space-y-6" data-testid="donation-view">
      <Section 
        title={`Donation #${donation.donationId}`} 
        kicker={<span className="mono">{donation.receiptNumber || "Pending Receipt"}</span>}
        right={
          <div className="flex items-center gap-3">
            <Badge tone={donation.donationType === "MATCHED" ? "warm" : "brand"}>{donation.donationType}</Badge>
            <div className="w-px h-6 bg-emerald-900/10 mx-1"></div>
            
            {!isEditing ? (
              <>
                <Button variant="secondary" onClick={() => setIsEditing(true)}>
                  <Edit2 className="w-4 h-4" /> Edit
                </Button>
                <button onClick={handleDelete} className="text-red-600 hover:text-red-800 transition-colors p-2" title="Delete Donation">
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            ) : (
              <Button variant="secondary" onClick={() => { setIsEditing(false); initForm(donation, purposes); }}>
                <X className="w-4 h-4" /> Cancel
              </Button>
            )}
          </div>
        }
      />

      {isEditing ? (
        <form onSubmit={handleUpdate} className="space-y-6 bg-white p-6 rounded-xl border border-emerald-900/10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
            <Select label="Donor" required value={f.donorId} onChange={upd("donorId")}>
              <option value="">— select donor —</option>
              {donors.map(d => <option key={d.donorId} value={d.donorId}>{d.donorCode} • {d.salutation ? `${d.salutation} ` : ""}{d.name}</option>)}
            </Select>
            <Input label="Donation date" type="date" required value={f.donationDate} onChange={upd("donationDate")} />
            
            <div className="flex flex-col gap-1">
              <div className="flex gap-2 items-end">
                <div className="w-32 shrink-0">
                  <Select label="Currency" required value={f.amountTypeId} onChange={upd("amountTypeId")} disabled={isUtilized}>
                    <option value="">— select —</option>
                    {amountTypes.map(a => <option key={a.amount_type_id} value={a.amount_type_id}>{a.code}</option>)}
                  </Select>
                </div>
                <div className="flex-1">
                  <Input label="Amount" type="number" required min="1" step="0.01" value={f.donatedAmount} onChange={upd("donatedAmount")} disabled={isUtilized} />
                </div>
              </div>
              
              {/* Dynamic Helper Text */}
              {isUtilized ? (
                <div className="text-[11px] text-red-600 mt-1 font-medium">
                  Cannot edit amount or currency because funds are already utilized.
                </div>
              ) : selectedCurrencyCode && selectedCurrencyCode !== 'INR' ? (
                <div className="flex items-center gap-1 text-[11px] text-amber-700 mt-1 bg-amber-50 p-1.5 rounded-md">
                  <Info className="w-3 h-3" />
                  Base INR equivalent will update automatically upon save.
                </div>
              ) : null}
            </div>

            <Select label="Payment mode" required value={f.paymentModeId} onChange={upd("paymentModeId")}>
              <option value="">— select mode —</option>
              {paymentModes.map(p => <option key={p.payment_mode_id} value={p.payment_mode_id}>{p.description}</option>)}
            </Select>

            <Input label="Transaction Ref Number" type="text" placeholder="Txn ID, Cheque No" value={f.transactionReferenceNumber} onChange={upd("transactionReferenceNumber")} />

            <Select label="Sector" value={f.sectorId} onChange={e=>setF(v=>({...v, sectorId: e.target.value, projectId:"", activityId:""}))}>
              <option value="">— select —</option>
              {sectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
            <Select label="Project" value={f.projectId} onChange={e=>setF(v=>({...v, projectId: e.target.value, activityId:""}))}>
              <option value="">— select —</option>
              {projects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
            <Select label="Activity" value={f.activityId} onChange={upd("activityId")}>
              <option value="">— select —</option>
              {activities.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>

            <Select label="Donation type" value={f.donationType} onChange={upd("donationType")}>
              <option value="SELF">Self</option>
              <option value="MATCHED">Matched by employer</option>
            </Select>
            <Select label="Academic year" value={f.academicYearId} onChange={upd("academicYearId")}>
              <option value="">— none —</option>
              {years.map(y => <option key={y.id} value={y.id}>{y.code}{y.isCurrent ? " (current)" : ""}</option>)}
            </Select>

            {f.donationType === "MATCHED" && (
              <>
                <Select label="Matched by (donor)" value={f.matchedByDonorId} onChange={upd("matchedByDonorId")}>
                  <option value="">— select employer/donor —</option>
                  {donors.filter(d => d.donorType === "CORPORATE").map(d => <option key={d.donorId} value={d.donorId}>{d.name}</option>)}
                </Select>
                <Input label="Matched amount" type="number" value={f.matchedAmount} onChange={upd("matchedAmount")} />
              </>
            )}
          </div>
          <div className="pt-4 border-t border-emerald-900/10 flex justify-end">
            <Button type="submit" disabled={busy}>
              <Save className="w-4 h-4 mr-2" /> Save Updates
            </Button>
          </div>
        </form>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card title="Donation Details">
              {/* Show original amount */}
              <Row k="Amount" v={fmtCurrency(donation.amount, displayCurrencyCode)} mono strong />
              
              {/* If it's foreign, show the Exchange Rate and INR equivalent below it */}
              {isForeignCurrency && (
                <>
                  <Row k="Exchange Rate" v={`1 ${displayCurrencyCode} = ₹${donation.exchangeRate || 1}`} mono />
                  <Row k="Base Amount" v={`${fmtCurrency(donation.baseAmount, "INR")} (INR)`} mono />
                </>
              )}
              
              <Row k="Date" v={fmtDate(donation.donationDate)} />
              <Row k="Payment Mode" v={donation.paymentMode || donation.paymentModeDescription} />
              <Row k="Txn Ref No." v={donation.transactionReferenceNumber} mono />
              <Row k="Receipt No." v={donation.receiptNumber} mono />
            </Card>
            
            <Card title="Donor Context">
              <div className="flex justify-between gap-3 mb-2">
                <span className="text-muted-foreground">Donor</span>
                <Link to={`/admin/donors/${donation.donorId}`} className="text-emerald-800 font-semibold hover:underline text-right">
                  {donation.salutation ? `${donation.salutation} ` : ""}{donation.donorName}
                </Link>
              </div>
              <Row k="Donation Type" v={donation.donationType} />
              <Row k="Purpose" v={donation.purposeName} />
              <Row k="Scope" v={donation.purposeType} />
            </Card>

            <Card title={`Fund Journey ${isForeignCurrency ? "(Base INR)" : ""}`}>
              <Row k="Allocated" v={fmtCurrency(donation.allocatedAmount, "INR")} mono />
              <Row k="Utilized" v={fmtCurrency(donation.utilizedAmount, "INR")} mono />
              <div className="mt-3 pt-3 border-t border-emerald-900/10">
                <Row k="Unallocated Balance" v={fmtCurrency(donation.remainingUnallocated, "INR")} mono strong />
              </div>
            </Card>
          </div>

          {donation.utilizations && donation.utilizations.length > 0 && (
            <div className="rounded-xl border border-emerald-900/10 bg-white p-6">
              <div className="overline mb-4">Utilization History</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead>
                    <tr className="text-[11px] uppercase tracking-widest text-emerald-800/70 border-b border-emerald-900/10">
                      <th className="py-2 pr-4">Date</th>
                      <th className="py-2 pr-4">Amount (INR)</th>
                      <th className="py-2 pr-4">Purpose</th>
                      <th className="py-2 pr-4">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {donation.utilizations.map((u, i) => (
                      <tr key={i} className="border-b border-emerald-900/5 last:border-0">
                        <td className="py-3 pr-4 text-muted-foreground">{fmtDate(u.date)}</td>
                        <td className="py-3 pr-4 font-mono font-semibold text-emerald-900">{fmtCurrency(u.amount, "INR")}</td>
                        <td className="py-3 pr-4">{u.purpose}</td>
                        <td className="py-3 pr-4 text-muted-foreground">{u.description || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      <div className="pt-4">
        <Link to="/admin/donations" className="text-emerald-800 text-sm font-semibold hover:underline">← Back to donations</Link>
      </div>
    </div>
  );
}

function Card({ title, children }){ 
  return <div className="rounded-xl border border-emerald-900/10 bg-white p-5"><div className="overline mb-3">{title}</div><div className="space-y-2 text-sm">{children}</div></div>; 
}
function Row({ k, v, mono, strong }){ 
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground">{k}</span>
      <span className={`${mono ? "font-mono" : ""} ${strong ? "font-bold text-emerald-900" : "font-medium text-emerald-950"} text-right break-all`}>
        {v || "—"}
      </span>
    </div>
  ); 
}